import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import CmsHtmlContent from '../../components/cms/CmsHtmlContent'
import { Calendar, ArrowLeft } from 'lucide-react'
import { newsApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import MediaImage from '../../components/ui/MediaImage'
import Button from '../../components/ui/Button'
import { fadeInUp } from '../../utils/animations'
import { formatDate } from '../../utils/date'

export default function NewsDetail() {
  const { slug } = useParams()
  const { t } = useTranslation()
  const { localized, isRTL } = useLanguage()
  const [article, setArticle] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    newsApi
      .getBySlug(slug)
      .then(({ data }) => setArticle(data.data || data))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false))
  }, [slug])

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center header-offset">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!article) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center header-offset">
        <p className="text-text/60">{t('pages.notFound')}</p>
        <Button to="/news" variant="primary" className="mt-4">
          {t('buttons.back')}
        </Button>
      </div>
    )
  }

  const content = localized(article, 'content') || localized(article, 'body')

  return (
    <article className="header-offset">
      <div className="relative h-64 overflow-hidden sm:h-80 md:h-96">
        <MediaImage
          src={article.image || article.featured_image}
          alt={localized(article, 'title')}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent" />
      </div>

      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-12">
        <Link
          to="/news"
          className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-accent"
        >
          <ArrowLeft className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
          {t('buttons.back')}
        </Link>

        <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
          <div className="mb-4 flex items-center gap-2 text-sm text-text/50">
            <Calendar className="h-4 w-4" />
            {t('news.publishedOn')}{' '}
            {article.published_at && formatDate(article.published_at)}
          </div>
          <h1 className="mb-6 text-2xl font-bold text-primary sm:text-3xl md:text-4xl">
            {localized(article, 'title')}
          </h1>
          {article.content_en || article.content_ar ? (
            <CmsHtmlContent
              content={content}
              className="prose prose-lg max-w-none prose-headings:text-primary"
            />
          ) : (
            <p className="text-lg leading-relaxed text-text/75">{content}</p>
          )}
        </motion.div>
      </div>
    </article>
  )
}
